import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/api';
import EditBuyerProfileModal from '../components/EditBuyerProfileModal';
import loadImage from '../assets/imageLoader'; 
import { 
    FaUser, FaWallet, FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt, 
    FaBirthdayCake, FaIdCard, FaVenusMars, FaLeaf, FaCheckCircle, FaClock
} from 'react-icons/fa';
import { Map, MapMarker } from '../components/ui/map';

function OwnerProfile() {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [wallet, setWallet] = useState(null);

  const fetchProfile = async () => {
    if (!user || !user.userId) return;
    try {
      const [profileRes, walletRes] = await Promise.all([
        apiClient.get(`/profiles/${user.userId}`),
        apiClient.get('/wallet')
      ]);
      setProfileData(profileRes.data);
      setWallet(walletRes.data);
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      if (!profileData) setError('Failed to load profile data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div></div>;
  if (error) return <div className="p-8 text-center text-red-500 font-bold bg-red-50 rounded-xl">{error}</div>;
  if (!profileData) return <div className="p-8 text-center text-slate-500">No profile data found.</div>;

  const { name, picture, date_of_birth, gender, nid_or_passport, location, User: userData, buyer_details } = profileData;
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const InfoCard = ({ icon: Icon, label, value }) => (
      <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-center gap-4 hover:bg-slate-100 transition-colors">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-slate-400 shadow-sm text-sm">
              <Icon />
          </div>
          <div className="overflow-hidden">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</p>
              <p className="font-bold text-slate-800 truncate" title={value}>{value || 'N/A'}</p>
          </div>
      </div>
  );

  return (
    <div className="font-sans min-h-screen pb-10">
      <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-black text-slate-800 tracking-tight">My Profile</h2>
            <p className="text-slate-500 font-medium">Manage your personal information and location</p>
          </div>
          <button 
            onClick={() => setShowEditModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg shadow-emerald-200 transition-all active:scale-95 flex items-center gap-2"
          >
            Edit Profile
          </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Col: Identity */}
        <div className="lg:col-span-1 space-y-8">
            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 flex flex-col items-center text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-emerald-500 to-teal-600"></div>
                <div className="relative mb-4 mt-8">
                    <img 
                      src={loadImage(picture)} 
                      alt="Profile" 
                      className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-xl bg-slate-100"
                    />
                    <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 border-4 border-white rounded-full"></div>
                </div>
                <h3 className="text-2xl font-black text-slate-800 mb-1">{name}</h3>
                <p className="text-slate-500 font-bold mb-4 flex items-center gap-1"><FaLeaf className="text-emerald-500"/> {userData?.username}</p>
                
                <div className="w-full flex flex-col gap-2">
                    <div className="w-full bg-slate-50 text-slate-600 py-3 rounded-xl font-bold border border-slate-100">
                        Buyer Account
                    </div>
                    {userData?.is_Identity_Verified ? (
                        <div className="bg-green-50 text-green-700 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1 border border-green-100">
                            <FaCheckCircle /> Verified
                        </div>
                    ) : (
                        <div className="bg-orange-50 text-orange-700 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1 border border-orange-100">
                            <FaClock /> Pending Verification
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-emerald-600 p-8 rounded-[2rem] shadow-lg shadow-emerald-200 text-white relative overflow-hidden group hover:scale-[1.02] transition-transform">
                <div className="relative z-10">
                    <p className="text-emerald-100 font-bold text-sm uppercase mb-1">Wallet Balance</p>
                    <h3 className="text-4xl font-black">
                        {wallet ? `${wallet.balance.toFixed(2)}` : '...'} <span className="text-xl font-bold opacity-70">{wallet?.currency_code || 'BDT'}</span>
                    </h3>
                    {wallet?.reserved_balance > 0 && (
                        <p className="mt-2 text-sm font-medium bg-emerald-700/50 inline-block px-3 py-1 rounded-full border border-emerald-500/50">
                            + {wallet.reserved_balance} Reserved
                        </p>
                    )}
                </div>
                <FaWallet className="absolute -bottom-6 -right-6 text-emerald-500/30 text-[10rem] group-hover:rotate-12 transition-transform duration-500" />
            </div>
        </div>

        {/* Right Col: Details */}
        <div className="lg:col-span-2 space-y-8">
            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100">
                <h3 className="text-lg font-black text-slate-800 mb-6 flex items-center gap-2">
                    <FaIdCard className="text-emerald-600" /> Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoCard icon={FaBuilding} label="Organization" value={buyer_details?.organisation} />
                    <InfoCard icon={FaEnvelope} label="Email" value={userData?.email} />
                    <InfoCard icon={FaPhone} label="Phone" value={userData?.contact_No} />
                    <InfoCard icon={FaBirthdayCake} label="Date of Birth" value={formatDate(date_of_birth)} />
                    <InfoCard icon={FaVenusMars} label="Gender" value={gender} />
                    <InfoCard icon={FaIdCard} label="NID / Passport" value={nid_or_passport} />
                </div>
            </div>

            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100">
                <h3 className="text-lg font-black text-slate-800 mb-6 flex items-center gap-2">
                    <FaIdCard className="text-blue-500" /> Business Credentials
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profileData.id_document_url ? (
                        <a href={loadImage(profileData.id_document_url)} target="_blank" rel="noopener noreferrer" className="flex items-center p-4 bg-slate-50 rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all group">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-emerald-600 shadow-sm group-hover:scale-110 transition-transform">
                                <FaIdCard size={20} />
                            </div>
                            <div className="ml-4 overflow-hidden">
                                <p className="text-sm font-bold text-slate-700 truncate">ID Document</p>
                                <p className={`text-[10px] font-black uppercase tracking-wider ${profileData.User?.is_Identity_Verified ? 'text-emerald-500' : 'text-orange-500'}`}>
                                    {profileData.User?.is_Identity_Verified ? 'Verified' : 'Pending Verification'}
                                </p>
                            </div>
                        </a>
                    ) : (
                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-center text-slate-400 text-xs font-bold">No ID Document Uploaded</div>
                    )}

                    {buyer_details?.company_document_url ? (
                        <a href={loadImage(buyer_details.company_document_url)} target="_blank" rel="noopener noreferrer" className="flex items-center p-4 bg-slate-50 rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-blue-600 shadow-sm group-hover:scale-110 transition-transform">
                                <FaBuilding size={20} />
                            </div>
                            <div className="ml-4 overflow-hidden">
                                <p className="text-sm font-bold text-slate-700 truncate">Company Doc</p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Business Proof</p>
                            </div>
                        </a>
                    ) : (
                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-center text-slate-400 text-xs font-bold">No Business Doc Uploaded</div>
                    )}
                </div>
            </div>

            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                     <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                        <FaMapMarkerAlt className="text-emerald-600" /> Address & Location
                    </h3>
                </div>
               
                <div className="space-y-4">
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Pinned Address</p>
                        <p className="font-bold text-slate-800 leading-relaxed">
                            {location?.granular_location || 'No specific location pinned on map.'}
                        </p>
                    </div>
                    
                    {/* Map Integration */}
                    {location?.lat && location?.lng ? (
                        <div className="h-64 rounded-2xl overflow-hidden border border-slate-200 relative z-0 mt-4 shadow-inner">
                            <Map 
                                center={[location.lng, location.lat]} 
                                zoom={14} 
                                className="h-full w-full"
                            >
                                <MapMarker longitude={location.lng} latitude={location.lat} />
                            </Map>
                        </div>
                    ) : (
                        <div className="h-40 bg-slate-50 rounded-2xl border border-dashed border-slate-300 flex items-center justify-center text-slate-400 mt-4">
                            <p className="text-sm font-bold flex items-center gap-2">
                                <FaMapMarkerAlt /> No location set on map. Edit profile to set it.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>

      </div>

      {/* Edit Modal */}
      <EditBuyerProfileModal 
        showModal={showEditModal} 
        onClose={() => setShowEditModal(false)} 
        buyerData={profileData}
        onSave={fetchProfile}
      />
    </div>
  );
}

export default OwnerProfile;